// ============================================================================
// Hiring Kanban — 7 stage columns (Antonela spec §4.1, 2026-05-28).
//
// Stages (left→right): Open · Interview · Onboarding · Filled · On Hold ·
// Disregarded · No Status. Filled / Disregarded / No Status render with a
// de-emphasized header (lighter text, smaller count) since they're terminal /
// housekeeping buckets.
//
// Columns are fixed width and scroll horizontally on every viewport so labels
// never truncate (spec). Cards drag between columns; the drop calls
// onStageChange with optimistic UI handled by the parent. A drop onto the
// Onboarding column is intercepted upstream to open the hand-off modal rather
// than writing the status directly.
//
// DnD: @dnd-kit/core, mirroring new-account-sop/sop-kanban.tsx. Pointer sensor
// with an 8px activation distance so taps still open the detail drawer.
//
// 2026-10-08 (item 818): each person arranges THEIR view of the board through
// the shared board view control — sort, pin, drag into their order. Saved per
// person in the database; changes no request.
// ============================================================================

import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  HIRING_STAGES,
  HIRING_STAGE_LABELS,
  HIRING_TERMINAL_STAGES,
  type HiringRequestStatus,
} from '../../shared/types';
import {
  arrangeCards,
  BoardCard,
  BoardViewControls,
  boardCollision,
  resolveBoardDrop,
  useBoardViewPrefs,
  type BoardViewApi,
} from '../../shared/board-view';
import type { HiringRequestRow } from './hooks/use-hiring-data';
import RequestCard from './request-card';

interface Props {
  rows: HiringRequestRow[];
  onSelect: (row: HiringRequestRow) => void;
  onStageChange?: (
    row: HiringRequestRow,
    next: HiringRequestStatus,
  ) => Promise<void> | void;
}

const stageOf = (r: HiringRequestRow): HiringRequestStatus =>
  (r.status ?? 'No_Status') as HiringRequestStatus;

const requestFacts = (r: HiringRequestRow) => ({
  id: r.request_id,
  createdAt: r.created_at ?? null,
  startDate: r.target_start_date ?? null,
});

export default function HiringKanban({ rows, onSelect, onStageChange }: Props) {
  const [activeDrag, setActiveDrag] = useState<HiringRequestRow | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );
  const view = useBoardViewPrefs('hiring');

  const columns = useMemo(
    () =>
      HIRING_STAGES.map((stage) => ({
        stage,
        label: HIRING_STAGE_LABELS[stage],
        deemphasized: HIRING_TERMINAL_STAGES.includes(stage),
        rows: arrangeCards(rows.filter((r) => stageOf(r) === stage), requestFacts, view.prefs),
      })),
    [rows, view.prefs],
  );

  function handleDragStart(e: DragStartEvent) {
    const id = String(e.active.id);
    setActiveDrag(rows.find((r) => r.request_id === id) ?? null);
  }

  async function handleDragEnd(e: DragEndEvent) {
    setActiveDrag(null);
    if (!onStageChange || !e.over) return;
    const row = rows.find((r) => r.request_id === String(e.active.id));
    if (!row) return;
    const drop = resolveBoardDrop(e.over.id, e.over.data.current as { columnId?: string } | undefined, stageOf(row));
    if (drop.kind === 'reorder') {
      const shown = columns.find((c) => c.stage === stageOf(row))?.rows.map((r) => r.request_id) ?? [];
      view.place(shown, row.request_id, drop.overCardId);
      return;
    }
    if (drop.kind !== 'move') return;
    const next = drop.columnId as HiringRequestStatus;
    if (!HIRING_STAGES.includes(next)) return;
    if (stageOf(row) === next) return; // no-op
    try {
      await onStageChange(row, next);
    } catch (err) {
      console.error('[hiring-kanban] stage change failed', err);
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={boardCollision}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <BoardViewControls view={view} hasStartDate className="mb-3" />
      <div className="flex gap-3 overflow-x-auto pb-2">
        {columns.map((c) => (
          <KanbanColumn
            key={c.stage}
            stage={c.stage}
            label={c.label}
            rows={c.rows}
            deemphasized={c.deemphasized}
            onSelect={onSelect}
            draggable={Boolean(onStageChange)}
            view={view}
          />
        ))}
      </div>

      <DragOverlay>
        {activeDrag ? (
          <div className="w-72 rotate-1">
            <ul>
              <RequestCard row={activeDrag} onClick={() => {}} />
            </ul>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

interface ColumnProps {
  stage: HiringRequestStatus;
  label: string;
  rows: HiringRequestRow[];
  deemphasized: boolean;
  onSelect: (row: HiringRequestRow) => void;
  draggable: boolean;
  view: BoardViewApi;
}

function KanbanColumn({
  stage,
  label,
  rows,
  deemphasized,
  onSelect,
  draggable,
  view,
}: ColumnProps) {
  const { isOver, setNodeRef } = useDroppable({ id: stage });
  const shownIds = rows.map((r) => r.request_id);
  return (
    <section
      ref={setNodeRef}
      className={`w-72 shrink-0 rounded-lg border p-2 transition ${
        isOver ? 'border-tcs-text bg-tcs-bg-muted' : 'border-transparent'
      }`}
    >
      <h2
        className={`mb-2 flex items-center justify-between px-1 font-semibold uppercase tracking-wide ${
          deemphasized
            ? 'text-[11px] text-tcs-text-muted'
            : 'text-xs text-tcs-text-muted'
        }`}
      >
        <span className="truncate" title={label}>
          {label}
        </span>
        <span
          className={`ml-2 shrink-0 rounded bg-tcs-surface px-1.5 py-0.5 ${
            deemphasized ? 'text-[9px] text-tcs-text-muted' : 'text-[10px] text-tcs-text-secondary'
          }`}
        >
          {rows.length}
        </span>
      </h2>
      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-tcs-border-strong bg-tcs-raised p-4 text-center text-xs text-tcs-text-muted">
          Empty.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <BoardCard
              key={r.request_id}
              id={r.request_id}
              columnId={stage}
              shownIds={shownIds}
              view={view}
              draggable={draggable}
              as="div"
            >
              <RequestCard row={r} onClick={onSelect} />
            </BoardCard>
          ))}
        </ul>
      )}
    </section>
  );
}
